import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { DashboardLayout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Ticket, Search, Pencil, Copy, Download, Palette, Building2, Users, X } from "lucide-react";
import { SkeletonStatCards, SkeletonTable } from "@/components/PageSkeleton";
import CertificateDesignDialog from "@/components/CertificateDesignDialog";
import StaffManagerDialog from "@/components/StaffManagerDialog";
import { usePaginatedList, Pagination } from "@/components/Pagination";
import { toast } from "sonner";

const FieldInput = ({ label, ...props }) => (
  <div>
    <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">{label}</label>
    <Input className="h-11 border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white" {...props} />
  </div>
);

export default function Level2Dashboard() {
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [certs, setCerts] = useState([]);
  const [institutions, setInstitutions] = useState([]);
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherCount, setVoucherCount] = useState(20);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [justGenerated, setJustGenerated] = useState(null);
  const [search, setSearch] = useState("");
  const [editCert, setEditCert] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [designInst, setDesignInst] = useState(null);
  const [staffOpen, setStaffOpen] = useState(false);
  // Authoritative stats — sourced from /api/admin/stats (count_documents on DB)
  const [adminStats, setAdminStats] = useState(null);

  const load = async () => {
    try {
      const [vRes, cRes, iRes, sRes] = await Promise.all([
        api.get("/vouchers", { params: { limit: 2000 } }),
        api.get("/certificates", { params: { limit: 1000 } }),
        api.get("/institutions", { params: { limit: 1000 } }),
        api.get("/admin/stats"),
      ]);
      setVouchers(vRes.data);
      setCerts(cRes.data);
      setInstitutions(iRes.data);
      setAdminStats(sRes.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const instById = useMemo(() => Object.fromEntries(institutions.map((i) => [i.id, i.name])), [institutions]);

  // Stats — trust /admin/stats (authoritative), fall back to fetched-list counts if the endpoint is not yet available.
  const usedVouchers = adminStats?.vouchers?.used ?? vouchers.filter((v) => v.used_count > 0).length;
  const availableVouchers = adminStats?.vouchers?.ready ?? vouchers.filter((v) => !v.bound_certificate_id && v.uses_remaining > 0).length;
  const totalCerts = adminStats?.certificates?.total ?? certs.length;
  const totalInsts = adminStats?.institutions?.active ?? institutions.length;

  const filteredCerts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return certs;
    return certs.filter((c) =>
      c.certificate_id.toLowerCase().includes(q) ||
      c.graduate_name.toLowerCase().includes(q) ||
      c.degree.toLowerCase().includes(q) ||
      (instById[c.institution_id] || "").toLowerCase().includes(q)
    );
  }, [certs, search, instById]);

  // Client-side pagination (15/page) for the three admin tables.
  const certsPage    = usePaginatedList(filteredCerts, { pageSize: 15 });
  const vouchersPage = usePaginatedList(vouchers,      { pageSize: 15, searchFields: ["code", "bound_certificate_id"] });
  const designPage   = usePaginatedList(institutions,  { pageSize: 15, searchFields: ["name", "email", "president", "category"] });

  const handleGenerateVouchers = async (e) => {
    e.preventDefault();
    setGenerating(true);
    setJustGenerated(null);
    try {
      const payload = { count: Number(voucherCount) };
      if (recipientEmail.trim()) payload.recipient_email = recipientEmail.trim();
      const { data } = await api.post("/vouchers/generate", payload);
      setJustGenerated(data);
      toast.success(`${data.length} voucher codes generated${recipientEmail.trim() ? ` · email queued to ${recipientEmail.trim()}` : ""}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to generate vouchers");
    } finally {
      setGenerating(false);
    }
  };

  const copyVouchers = () => {
    if (!justGenerated) return;
    navigator.clipboard.writeText(justGenerated.map((v) => v.code).join("\n"));
    toast.success("Voucher codes copied to clipboard");
  };

  const downloadVouchers = () => {
    if (!justGenerated) return;
    // CSV with expiry — verifiers can print / archive with the promised
    // 5-year validity clearly stamped on the receipt.
    const rows = [
      ["Voucher Code", "Expires On", "Uses Allowed"],
      ...justGenerated.map((v) => [
        v.code,
        v.expires_at ? new Date(v.expires_at).toLocaleDateString() : "—",
        String(v.uses_remaining ?? 3),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `certiseal-vouchers-${Date.now()}.csv`;
    a.click();
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editCert) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/certificates/${editCert.id}`, {
        graduate_name: editCert.graduate_name,
        degree: editCert.degree,
        major: editCert.major,
        graduation_date: editCert.graduation_date,
        gpa: editCert.gpa || null,
        honors: editCert.honors || null,
      });
      setCerts((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      toast.success("Certificate updated");
      setEditCert(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout title="Operations · Level-2 Admin" subtitle="Generate global vouchers and edit graduate records across all institutions.">
      {/* Quick actions */}
      <div className="mb-6 flex justify-end">
        <Button variant="outline" onClick={() => setStaffOpen(true)} data-testid="manage-staff-btn">
          <Users className="w-4 h-4 mr-2" /> Manage team
        </Button>
      </div>
      {loading ? (
        <div className="mb-8">
          <SkeletonStatCards count={4} />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
          <StatCard label="Vouchers ready" value={availableVouchers} />
          <StatCard label="Vouchers used" value={usedVouchers} />
          <StatCard label="Certificates" value={totalCerts} />
          <StatCard label="Institutions" value={totalInsts} />
        </div>
      )}

      <Tabs defaultValue="vouchers" className="w-full">
        <TabsList className="bg-slate-100 dark:bg-slate-800 p-1">
          <TabsTrigger value="vouchers" data-testid="l2-tab-vouchers">Vouchers ({adminStats?.vouchers?.total ?? vouchers.length})</TabsTrigger>
          <TabsTrigger value="records" data-testid="l2-tab-records">Graduate records ({totalCerts})</TabsTrigger>
          <TabsTrigger value="design" data-testid="l2-tab-design">Certificate design</TabsTrigger>
        </TabsList>

        {/* Vouchers */}
        <TabsContent value="vouchers" className="mt-6">
          <Card className="border-slate-200 dark:border-slate-800">
            <div className="p-6 flex items-center justify-between border-b border-slate-200 dark:border-slate-800 flex-wrap gap-3">
              <div>
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">Global voucher codes</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Every code you generate here validates any institution's certificate. 3 uses per code before it locks.</p>
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative w-full sm:w-72">
                  <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input
                    value={vouchersPage.query}
                    onChange={(e) => vouchersPage.setQuery(e.target.value)}
                    placeholder="Search code or bound cert…"
                    className="h-10 pl-9 pr-9"
                    data-testid="l2-voucher-search-input"
                  />
                  {vouchersPage.query && (
                    <button type="button" onClick={() => vouchersPage.setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1" aria-label="Clear search">
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              <Dialog open={voucherOpen} onOpenChange={(o) => { setVoucherOpen(o); if (!o) setJustGenerated(null); }}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700 font-display font-semibold" data-testid="l2-generate-vouchers-btn">
                    <Ticket className="w-4 h-4 mr-2" /> Generate vouchers
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader><DialogTitle>Generate voucher batch</DialogTitle></DialogHeader>
                  {!justGenerated ? (
                    <form onSubmit={handleGenerateVouchers} className="space-y-4">
                      <FieldInput label="How many codes?" type="number" min={1} max={1000} value={voucherCount} onChange={(e) => setVoucherCount(e.target.value)} data-testid="l2-voucher-count-input" />
                      <FieldInput label="Email recipient (optional)" type="email" value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} placeholder="ops@your-partner.org" data-testid="l2-voucher-email-input" />
                      <DialogFooter>
                        <Button type="submit" disabled={generating} className="bg-blue-600 hover:bg-blue-700 font-display font-semibold" data-testid="l2-voucher-submit-btn">
                          {generating ? "Generating…" : `Generate ${voucherCount} codes`}
                        </Button>
                      </DialogFooter>
                    </form>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-slate-600 dark:text-slate-300">{justGenerated.length} codes ready. Copy or download before closing.</p>
                      <div className="max-h-56 overflow-y-auto border border-slate-200 dark:border-slate-800 rounded-lg p-3 font-mono text-sm">
                        {justGenerated.map((v) => (<div key={v.id}>{v.code}</div>))}
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={copyVouchers} variant="outline" className="flex-1" data-testid="l2-copy-vouchers-btn"><Copy className="w-4 h-4 mr-2" /> Copy</Button>
                        <Button onClick={downloadVouchers} variant="outline" className="flex-1" data-testid="l2-download-vouchers-btn"><Download className="w-4 h-4 mr-2" /> Download</Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
              </div>

            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Uses left</TableHead>
                  <TableHead>Bound to</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={5} className="p-0">
                      <SkeletonTable rows={4} cols={5} testId="l2-vouchers-skeleton" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && vouchersPage.items.map((v) => (
                  <TableRow key={v.id} data-testid={`voucher-row-${v.id}`}>
                    <TableCell className="font-mono text-xs">{v.code}</TableCell>
                    <TableCell>
                      {v.uses_remaining <= 0 ? <Badge variant="secondary">Depleted</Badge>
                        : v.bound_certificate_id ? <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Bound</Badge>
                          : <Badge className="bg-blue-100 text-blue-800 border-blue-200">Available</Badge>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{v.uses_remaining}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-500" data-testid={`voucher-bound-${v.id}`}>
                      {v.bound_certificate_id || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">{new Date(v.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!loading && vouchersPage.totalFiltered === 0 && (
              <div className="p-8 text-center text-sm text-slate-500">
                {vouchersPage.query.trim() ? `No vouchers match "${vouchersPage.query.trim()}".` : "No vouchers yet. Generate a batch to get started."}
              </div>
            )}
            {!loading && (
              <Pagination
                page={vouchersPage.page}
                totalPages={vouchersPage.totalPages}
                setPage={vouchersPage.setPage}
                rangeStart={vouchersPage.rangeStart}
                rangeEnd={vouchersPage.rangeEnd}
                totalFiltered={vouchersPage.totalFiltered}
                testId="l2-voucher-pagination"
              />
            )}
          </Card>
        </TabsContent>

        {/* Records */}
        <TabsContent value="records" className="mt-6">
          <Card className="border-slate-200 dark:border-slate-800">
            <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white">Graduate records</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Search across every institution. Click a row to edit the record.</p>
              </div>
              <div className="relative w-full sm:w-80">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, cert ID, degree, institution…" className="pl-9 h-10" data-testid="l2-cert-search-input" />
              </div>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cert ID</TableHead>
                  <TableHead>Graduate</TableHead>
                  <TableHead>Degree</TableHead>
                  <TableHead>Institution</TableHead>
                  <TableHead>Graduated</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={6} className="p-0">
                      <SkeletonTable rows={5} cols={5} testId="l2-records-skeleton" />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && certsPage.items.map((c) => (
                  <TableRow key={c.id} data-testid={`cert-row-${c.id}`}>
                    <TableCell className="font-mono text-xs">{c.certificate_id}</TableCell>
                    <TableCell className="font-medium">{c.graduate_name}</TableCell>
                    <TableCell className="text-sm">{c.degree}</TableCell>
                    <TableCell className="text-xs text-slate-500">{instById[c.institution_id] || c.institution_id}</TableCell>
                    <TableCell className="text-xs">{c.graduation_date}</TableCell>
                    <TableCell>
                      <Button size="sm" variant="outline" onClick={() => setEditCert({ ...c })} data-testid={`edit-cert-${c.id}-btn`}>
                        <Pencil className="w-3 h-3 mr-1.5" /> Edit
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {!loading && filteredCerts.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No records match your search.</div>}
            {!loading && filteredCerts.length > 0 && (
              <Pagination
                page={certsPage.page}
                totalPages={certsPage.totalPages}
                setPage={certsPage.setPage}
                rangeStart={certsPage.rangeStart}
                rangeEnd={certsPage.rangeEnd}
                totalFiltered={certsPage.totalFiltered}
                testId="l2-records-pagination"
              />
            )}
          </Card>
        </TabsContent>

        {/* Certificate Design tab — L2 can edit any institution's template */}
        <TabsContent value="design" className="mt-6">
          <Card className="border-slate-200 dark:border-slate-800">
            <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-display font-bold text-lg text-slate-900 dark:text-white flex items-center gap-2">
                  <Palette className="w-5 h-5 text-blue-600" /> Certificate design
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
                  Upload header, signatures, seal, and set boilerplate text for each institution's certificates.
                </p>
              </div>
              <div className="relative w-full sm:w-72">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <Input
                  value={designPage.query}
                  onChange={(e) => designPage.setQuery(e.target.value)}
                  placeholder="Search institutions…"
                  className="h-10 pl-9 pr-9"
                  data-testid="l2-design-search-input"
                />
                {designPage.query && (
                  <button type="button" onClick={() => designPage.setQuery("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 p-1" aria-label="Clear search">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-slate-900">
              {loading && (
                <div className="p-6"><SkeletonTable rows={3} cols={2} testId="l2-design-skeleton" /></div>
              )}
              {!loading && designPage.totalFiltered === 0 && (
                <div className="p-8 text-center text-sm text-slate-500">
                  {designPage.query.trim() ? `No institutions match "${designPage.query.trim()}".` : "No institutions yet."}
                </div>
              )}
              {!loading && designPage.items.map((inst) => (
                <div key={inst.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`design-inst-row-${inst.id}`}>
                  <div className="w-10 h-10 rounded-md bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-slate-900 dark:text-white truncate">{inst.name}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">Est. {inst.year_established} · {inst.category}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setDesignInst(inst)} data-testid={`l2-design-btn-${inst.id}`}>
                    <Palette className="w-3.5 h-3.5 mr-1.5" /> Edit design
                  </Button>
                </div>
              ))}
            </div>
            {!loading && designPage.totalFiltered > 0 && (
              <Pagination
                page={designPage.page}
                totalPages={designPage.totalPages}
                setPage={designPage.setPage}
                rangeStart={designPage.rangeStart}
                rangeEnd={designPage.rangeEnd}
                totalFiltered={designPage.totalFiltered}
                testId="l2-design-pagination"
              />
            )}
          </Card>
        </TabsContent>
      </Tabs>

      {/* Certificate design dialog */}
      <CertificateDesignDialog
        institution={designInst}
        open={!!designInst}
        onOpenChange={(o) => !o && setDesignInst(null)}
        onSaved={(updated) => {
          setInstitutions((list) => list.map((i) => (i.id === updated.id ? { ...i, ...updated } : i)));
          setDesignInst(updated);
        }}
      />

      {/* Staff manager dialog */}
      <StaffManagerDialog open={staffOpen} onOpenChange={setStaffOpen} />

      {/* Edit dialog */}
      <Dialog open={!!editCert} onOpenChange={(o) => { if (!o) setEditCert(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit certificate {editCert?.certificate_id}</DialogTitle></DialogHeader>
          {editCert && (
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <FieldInput label="Graduate name" value={editCert.graduate_name} onChange={(e) => setEditCert({ ...editCert, graduate_name: e.target.value })} data-testid="edit-graduate-name" />
              <FieldInput label="Degree" value={editCert.degree} onChange={(e) => setEditCert({ ...editCert, degree: e.target.value })} data-testid="edit-degree" />
              <FieldInput label="Major / field (optional)" value={editCert.major || ""} onChange={(e) => setEditCert({ ...editCert, major: e.target.value })} data-testid="edit-major" />
              <FieldInput label="Graduation date (YYYY-MM-DD)" value={editCert.graduation_date} onChange={(e) => setEditCert({ ...editCert, graduation_date: e.target.value })} data-testid="edit-graduation-date" />
              <div className="grid grid-cols-2 gap-3">
                <FieldInput label="GPA" value={editCert.gpa || ""} onChange={(e) => setEditCert({ ...editCert, gpa: e.target.value })} data-testid="edit-gpa" />
                <FieldInput label="Honors" value={editCert.honors || ""} onChange={(e) => setEditCert({ ...editCert, honors: e.target.value })} data-testid="edit-honors" />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditCert(null)}>Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="edit-cert-save-btn">
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function StatCard({ label, value }) {
  return (
    <Card className="p-5 border-slate-200 dark:border-slate-800">
      <div className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 font-semibold">{label}</div>
      <div className="mt-2 font-display font-extrabold text-3xl text-slate-900 dark:text-white">{value}</div>
    </Card>
  );
}
